import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { getHot, searchMovies } from "@/utils/movie";
import { useSEO } from "@/hooks/useSEO";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const typeFilter = searchParams.get("type") ?? "all";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => {
    const base = searchMovies(keyword);
    if (typeFilter === "all") return base;
    return base.filter((m) => m.type === typeFilter);
  }, [keyword, typeFilter]);

  useSEO({
    title: keyword ? `“${keyword}”的搜索结果 - 欧美一级视频` : "影视搜索 - 欧美一级视频",
    description: `在欧美一级视频中搜索“${keyword || "影视作品"}”，查找相关的欧美电影与电视剧资料。`,
    keywords: `${keyword},欧美影视搜索,欧美电影搜索,欧美电视剧查找`,
  });

  const hot = getHot(4);

  const submit = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("q", value.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  const changeType = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("type");
    else next.set("type", value);
    setSearchParams(next);
  };

  const typeTabs = [
    { label: "全部", value: "all" },
    { label: "电影", value: "movie" },
    { label: "电视剧", value: "tv" },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
      <h1 className="text-2xl font-black text-foreground-950 md:text-3xl">搜索结果</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
        className="mt-4 flex flex-col gap-3 sm:flex-row"
      >
        <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称或关键词…"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
          />
          {input ? (
            <button
              type="button"
              aria-label="清空"
              onClick={() => setInput("")}
              className="cursor-pointer text-foreground-400 hover:text-foreground-700"
            >
              <i className="ri-close-circle-line"></i>
            </button>
          ) : null}
        </div>
        <button
          type="submit"
          className="flex h-11 shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 hover:bg-primary-600"
        >
          <i className="ri-search-2-line"></i>
          搜索
        </button>
      </form>

      {keyword ? (
        <p className="mt-5 text-sm text-foreground-600">
          关键词 <span className="font-semibold text-primary-600">“{keyword}”</span> 共找到{" "}
          <span className="font-semibold text-primary-600">{results.length}</span> 部作品
        </p>
      ) : (
        <p className="mt-5 text-sm text-foreground-600">请输入关键词，搜索你感兴趣的欧美影视作品。</p>
      )}

      <div className="mt-4 flex w-fit items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
        {typeTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => changeType(tab.value)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              typeFilter === tab.value
                ? "bg-primary-500 font-semibold text-background-50"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {keyword && results.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <p className="text-base font-semibold text-foreground-900">没有找到与“{keyword}”相关的作品</p>
          <p className="max-w-md text-sm text-foreground-600">
            试试更换关键词，或浏览下方热门内容。
          </p>
          <div className="mt-2 grid w-full max-w-3xl grid-cols-2 gap-3 px-4 sm:grid-cols-4">
            {hot.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </div>
      ) : results.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {results.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center text-sm text-foreground-600">
          输入关键词后，这里会显示匹配的影视作品。
        </div>
      )}
    </div>
  );
}